/**
 * Raven → Flock key handoff over the URL fragment.
 *
 *   theflock.ink/tune#keys=<base64-encoded JSON>
 *
 * The base64 payload decodes to: { keys: HandoffKey[] }
 * where HandoffKey = { publicKey: base64, encodingRule, label }.
 *
 * Keys live ONLY in JavaScript module memory. There is NO localStorage,
 * IndexedDB, cookie, or any other persistence path. Tab close = keys gone
 * by design.
 */

import type { EncodingRule } from './raven-core/types.js';
import { deriveFrequency } from './frequency.js';

export interface HandoffKey {
  publicKey: string;
  encodingRule: EncodingRule;
  label: string;
}

export interface LoadedKey extends HandoffKey {
  frequency: string;
  publicKeyBytes: Uint8Array;
}

let loaded: LoadedKey[] | null = null;

export interface ParseResult {
  ok: boolean;
  keys?: HandoffKey[];
  reason?: string;
}

export function parseHandoffFragment(fragment: string): ParseResult {
  if (fragment === '' || fragment === '#') return { ok: false, reason: 'no_fragment' };

  const trimmed = fragment.startsWith('#') ? fragment.slice(1) : fragment;
  const params = new URLSearchParams(trimmed);
  const raw = params.get('keys');
  if (raw === null) return { ok: false, reason: 'no_keys_param' };

  let json: string;
  try {
    json = base64Decode(raw);
  } catch {
    return { ok: false, reason: 'base64_decode_failed' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { ok: false, reason: 'json_parse_failed' };
  }

  if (!isHandoffPayload(parsed)) {
    return { ok: false, reason: 'invalid_shape' };
  }

  return { ok: true, keys: parsed.keys };
}

interface HandoffPayload {
  keys: HandoffKey[];
}

function isHandoffPayload(value: unknown): value is HandoffPayload {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as { keys?: unknown };
  if (!Array.isArray(v.keys)) return false;
  return v.keys.every(isHandoffKey);
}

function isHandoffKey(v: unknown): v is HandoffKey {
  if (typeof v !== 'object' || v === null) return false;
  const k = v as { publicKey?: unknown; encodingRule?: unknown; label?: unknown };
  return (
    typeof k.publicKey === 'string' &&
    typeof k.label === 'string' &&
    typeof k.encodingRule === 'object' &&
    k.encodingRule !== null
  );
}

export function base64Decode(input: string): string {
  const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
  return atob(normalized);
}

export function base64ToBytes(input: string): Uint8Array {
  const decoded = base64Decode(input);
  const bytes = new Uint8Array(decoded.length);
  for (let i = 0; i < decoded.length; i++) {
    bytes[i] = decoded.charCodeAt(i);
  }
  return bytes;
}

/**
 * Read window.location.hash, parse, derive frequencies, store in memory,
 * scrub the URL bar. Idempotent — subsequent calls without a fragment
 * preserve the previously loaded keys.
 */
export async function loadKeysFromLocation(): Promise<LoadedKey[] | null> {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  const result = parseHandoffFragment(hash);
  if (!result.ok || result.keys === undefined) return loaded;

  const keys: LoadedKey[] = [];
  for (const k of result.keys) {
    const bytes = base64ToBytes(k.publicKey);
    const frequency = await deriveFrequency(bytes);
    keys.push({ ...k, frequency, publicKeyBytes: bytes });
  }
  loaded = keys;

  // Scrub the URL so the keys don't sit in the address bar / browser history.
  const cleanUrl = `${window.location.pathname}${window.location.search}#/frequencies`;
  window.history.replaceState({}, '', cleanUrl);

  return keys;
}

export function getLoadedKeys(): LoadedKey[] | null {
  return loaded;
}

export function getKeyForFrequency(frequency: string): LoadedKey | undefined {
  if (loaded === null) return undefined;
  return loaded.find((k) => k.frequency === frequency);
}

export function resetLoadedKeysForTest(): void {
  loaded = null;
}
